import type { AxiosRequestConfig } from 'axios';
import { monitoringInstance } from '@/shared/api/api-instances/monitoring/api-instance';
import { Page, PaginationParams } from '@/shared/api/pagination';

import type {
  GroupedVMAgent,
  VMAgent,
  VMAgentItem,
  VMAgentTypeCreateRequest,
  VMAgentTypeUpdateRequest,
} from '../model/vm-agent';
import type {
  PrometheusResponse,
  QueryParams,
  QueryPreviewParams,
  QueryRangeParams,
  QueryRangePreviewParams,
} from '@/entities/dashboards/dashboard-widget/@x/vm-agent';
import { ImportEntities } from '@/shared/api/import-contracts';

const baseURL = '/api/monitoring/v4/';
const defaultOptions = { baseURL };

export class VMAgentsAPI {
  static query(params: QueryParams, options?: AxiosRequestConfig) {
    return monitoringInstance.post<PrometheusResponse>(
      `/vmagent/proxy/query`,
      params,
      { ...defaultOptions, ...options },
    );
  }

  static queryPreview(
    params: QueryPreviewParams,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<PrometheusResponse>(
      `/vmagent/proxy/query/preview`,
      params,
      { ...defaultOptions, ...options },
    );
  }

  static queryRange(payload: QueryRangeParams, options?: AxiosRequestConfig) {
    return monitoringInstance.post<PrometheusResponse>(
      `/vmagent/proxy/query_range`,
      payload,
      { ...defaultOptions, ...options },
    );
  }

  static queryRangePreview(
    payload: QueryRangePreviewParams,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<PrometheusResponse>(
      `/vmagent/proxy/query_range/preview`,
      payload,
      { ...defaultOptions, ...options },
    );
  }

  static prometheusQuery(
    { path, params }: { path: string; params?: Record<string, string> },
    options?: AxiosRequestConfig,
  ) {
    const getParams = {
      path: path,
      ...params,
    };
    return monitoringInstance.get(`/prometheus/raw_query`, {
      ...options,
      params: getParams,
    });
  }

  static getAllMetrics({ params = {} } = { params: {} }) {
    return VMAgentsAPI.prometheusQuery({
      path: 'label/__name__/values',
      params: { ...params },
    });
  }

  static getAllLabels({ params = {} } = { params: {} }) {
    return VMAgentsAPI.prometheusQuery({
      path: 'labels',
      params: { ...params },
    });
  }

  static getGroupedVMAgents(options?: AxiosRequestConfig) {
    return monitoringInstance.get<GroupedVMAgent[]>(`/jobs/grouped_vmagents`, {
      ...defaultOptions,
      ...options,
    });
  }

  static getVMAgents(params: PaginationParams, options?: AxiosRequestConfig) {
    return monitoringInstance.get<Page<VMAgentItem>>('/vmagents', {
      ...defaultOptions,
      ...options,
      params,
    });
  }

  static getVMAgentByName(name: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<VMAgent>(`/vmagent/${name}`, {
      ...defaultOptions,
      ...options,
    });
  }

  static createVMAgent(
    payload: VMAgentTypeUpdateRequest,
    options: AxiosRequestConfig = {},
  ) {
    return monitoringInstance.post(`/vmagent`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static updateVMAgent(
    name: string,
    payload: VMAgentTypeCreateRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.put(`/vmagent/${name}`, payload, {
      ...defaultOptions,
      ...options,
    });
  }

  static deleteVMAgent(
    name: string,
    force: boolean = false,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.delete(`/vmagent/${name}`, {
      ...defaultOptions,
      ...options,
      params: { ...options?.params, force },
    });
  }

  static analyzeVMAgentEntities(json: string, options?: AxiosRequestConfig) {
    return monitoringInstance.post('/import/vmagent/analyze', json, {
      ...defaultOptions,
      ...options,
    });
  }

  static validateImport(json: string, options?: AxiosRequestConfig) {
    return monitoringInstance.post('/import/vmagent/validate', json, {
      ...defaultOptions,
      ...options,
    });
  }

  static executeVMAgentEntities(
    data: ImportEntities,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post('/import/vmagent/execute', data, {
      ...defaultOptions,
      ...options,
    });
  }

  static exportVMAgents(vmagent_names: string[], options?: AxiosRequestConfig) {
    return monitoringInstance.get('/vmagent/export/json', {
      ...defaultOptions,
      ...options,
      params: { vmagent_names },

      paramsSerializer: () => {
        const searchParams = new URLSearchParams();
        vmagent_names.forEach((name) =>
          searchParams.append('vmagent_names', name),
        );
        return searchParams.toString();
      },
    });
  }
}
